import { message } from 'antd';
import { getCodigosPostales } from 'api/catalogos';

// Utilidades para pasar a un formulario de Dirección el domicilio leído de un documento (INE,
// constancia fiscal o comprobante de domicilio). El servidor ya expande las abreviaturas
// ("C." → CALLE, "COL." → COLONIA...); aquí se empata con los catálogos del sistema.

export const normalizarTexto = (texto) => String(texto || '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .replace(/[^A-Za-z0-9Ññ ]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()
  .toUpperCase();

// Tipo de vialidad del catálogo que corresponde al texto leído (CALLE, AVENIDA...), o undefined.
export const buscarTipoDeVialidad = (tipos = [], leido) => {
  const buscado = normalizarTexto(leido);
  if (!buscado) return undefined;
  const lista = tipos.map((tipo) => ({ tipo, nombre: normalizarTexto(tipo.descripcion) }));
  return (lista.find((item) => item.nombre === buscado)
    || lista.find((item) => item.nombre.startsWith(buscado) || buscado.startsWith(item.nombre))
    || {}).tipo;
};

// Colonia de la lista del código postal que corresponde a la leída, o undefined.
export const buscarColonia = (colonias = [], leida) => {
  const buscada = normalizarTexto(leida);
  if (!buscada) return undefined;
  const lista = colonias.map((item) => ({ item, nombre: normalizarTexto(item.colonia) }));
  return (lista.find((c) => c.nombre === buscada)
    || lista.find((c) => c.nombre.includes(buscada) || buscada.includes(c.nombre))
    || {}).item;
};

// Dirección "Exacta" (catálogos): llena tipo de vialidad, calle, números, código postal (con su
// estado, municipio y colonias) y elige la colonia leída si está en la lista del código postal.
// `form` es el formulario de Dirección; `setColonias` actualiza las opciones del campo Colonia.
export const aplicarDomicilioExacto = async ({
  domicilio, form, tiposDeVialidades, setColonias,
}) => {
  const tipo = buscarTipoDeVialidad(tiposDeVialidades, domicilio.tipo_de_vialidad);
  const valores = {
    tipo_de_vialidad: tipo?.id,
    calle_principal: domicilio.calle || undefined,
    numero_exterior: domicilio.numero_exterior || undefined,
    numero_interior: domicilio.numero_interior || undefined,
  };
  const faltantes = [];
  if (!tipo && domicilio.tipo_de_vialidad) faltantes.push('el Tipo de Vialidad');

  if (domicilio.codigo_postal) {
    valores.d_codigo = domicilio.codigo_postal;
    const codigos = await getCodigosPostales({ d_codigo: domicilio.codigo_postal });
    if (codigos.length) {
      const opciones = codigos.map((item) => ({ id: item.id, colonia: item.d_asenta }));
      setColonias(opciones);
      const [primero] = codigos;
      const colonia = buscarColonia(opciones, domicilio.colonia)
        || (codigos.length === 1 ? opciones[0] : undefined);
      Object.assign(valores, {
        pais_codigo_postal: 'México',
        estado: primero?.municipio?.estado?.nombre_de_AGEE || '',
        municipio: primero?.municipio?.nom_mun || '',
        colonia: colonia?.colonia ?? null,
        codigo_postal: colonia?.id ?? null,
      });
      if (!colonia) faltantes.push(`la Colonia${domicilio.colonia ? ` (${domicilio.colonia})` : ''}`);
    } else {
      faltantes.push('el Código Postal (no se encontró en el catálogo)');
    }
  } else {
    faltantes.push('el Código Postal');
  }

  form.setFieldsValue(valores);
  message.success('Se llenó el domicilio. Verifique que sea correcto.');
  if (faltantes.length) message.warning(`Revise manualmente ${faltantes.join(', ')}.`, 7);
};

// Dirección "General" (texto libre): vialidad completa, números, colonia, código postal,
// municipio, estado y la dirección conjunta.
export const aplicarDomicilioGeneral = ({ domicilio, form }) => {
  const vialidad = [domicilio.tipo_de_vialidad, domicilio.calle].filter(Boolean).join(' ');
  const conjunta = [
    [vialidad, domicilio.numero_exterior, domicilio.numero_interior
      ? `INTERIOR ${domicilio.numero_interior}` : null].filter(Boolean).join(' '),
    domicilio.colonia ? `COLONIA ${domicilio.colonia}` : null,
    domicilio.codigo_postal ? `CODIGO POSTAL ${domicilio.codigo_postal}` : null,
    domicilio.municipio,
    domicilio.estado,
  ].filter(Boolean).join(', ');

  form.setFieldsValue({
    vialidad: vialidad || undefined,
    no_exterior: domicilio.numero_exterior || undefined,
    no_interior: domicilio.numero_interior || undefined,
    colonia: domicilio.colonia || undefined,
    codigo_postal: domicilio.codigo_postal || undefined,
    municipio: domicilio.municipio || undefined,
    estado: domicilio.estado || undefined,
    descripcion_conjunta: conjunta || undefined,
  });
  message.success('Se llenó el domicilio. Verifique que sea correcto.');
};
